import QRCode from 'qrcode';
import { useMemo } from 'react';

import { cx } from './cx';
import styles from './QrCode.module.css';

export interface QrCodeProps {
  value: string;
  /** Outer edge in px incl. the white frame (Figma Util/QR: 400 / 520). */
  size?: number;
  /** Quiet zone in modules (keep ≥ 2: the frame's rounded corners sit inside it). */
  margin?: number;
  className?: string;
}

/** Builds one SVG path covering every dark module (fast to render, scales crisply). */
export function qrPath(value: string, margin: number): { path: string; modules: number } {
  const qr = QRCode.create(value, { errorCorrectionLevel: 'M' });
  const size = qr.modules.size;
  const data = qr.modules.data;
  let path = '';
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) {
      if (data[r * size + c]) path += `M${c + margin} ${r + margin}h1v1h-1z`;
    }
  }
  return { path, modules: size + margin * 2 };
}

/** Real, scannable QR rendered as inline SVG (pairing / join URLs). */
export function QrCode({ value, size = 400, margin = 2, className }: QrCodeProps) {
  const { path, modules } = useMemo(() => qrPath(value, margin), [value, margin]);
  return (
    <div
      className={cx(styles.frame, className)}
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${modules} ${modules}`}
        preserveAspectRatio="xMidYMid meet"
        shapeRendering="crispEdges"
        role="img"
        aria-label={value}
        data-testid="qr"
      >
        <rect width={modules} height={modules} fill="#ffffff" />
        <path d={path} fill="#0b0b14" />
      </svg>
    </div>
  );
}
